import React, { createContext, useState, useContext, useEffect } from 'react';
import { refreshUserSchoolContext } from '../utils/auth';

// API base URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

// Create the user context
export const UserContext = createContext(null);

// Create a provider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get user info from localStorage on component mount
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        // Parse the stored user and ensure school_id is set correctly
        const parsedUser = JSON.parse(storedUser);
        const updatedUser = refreshUserSchoolContext(parsedUser);
        setUser(updatedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Function to update user data
  const updateUser = (userData) => {
    // Ensure school_id is set correctly before updating state
    const updatedUser = userData ? refreshUserSchoolContext(userData) : null;
    
    setUser(updatedUser);
    if (updatedUser) {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } else {
      localStorage.removeItem('user');
    }
  };

  // Function to refresh user data from the API
  const refreshUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh user data');
      }
      
      const userData = await response.json();
      updateUser(userData);
      return userData;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      return null;
    }
  };

  // Function to log out user
  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  // Compute authentication state based on both user object and access token
  const isAuthenticated = !!user && !!localStorage.getItem('accessToken');

  return (
    <UserContext.Provider value={{ user, loading, updateUser, refreshUser, logout, isAuthenticated }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = () => useContext(UserContext); 